import React, {ChangeEvent, FC, KeyboardEvent, ReactElement, useCallback, useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import {useHistory} from 'react-router-dom';
import {useSearchContext, useSimpleSearchContext} from '@context';
import {getCbDebouncer} from '@utils';
import {strings} from '@constants';
import SearchButton from './SearchButton';
import SourceLabel from '../source-label/SourceLabel';

const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative'
    },
    searchTextField: {
        width: '100%'
    },
    searchInput: {
        paddingLeft: theme.spacing(2.5),
        paddingTop: theme.spacing(2.185),
        paddingRight: theme.spacing(2.5),
        paddingBottom: theme.spacing(2.185),
    },
    searchResults: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[8],
        overflow: 'auto',
        position: 'absolute',
        width: '100%',
        wordBreak: 'break-word',
        zIndex: 1
    },
    selectedResult: {
        backgroundColor: 'rgba(0, 0, 0, 0.05) !important'
    }
}));

const debounceCb = getCbDebouncer();

const SearchBox: FC = (): ReactElement => {
    const classes = useStyles();
    const history = useHistory();
    const {updateFilter, getSearchPath} = useSearchContext();
    const {searchString, updateSearchString, result} = useSimpleSearchContext();
    const searchInput = useRef<any>();
    const [shadowSearchString, setShadowSearchString] = useState<string>(searchString);
    const [searchFieldFocused, setSearchFieldFocused] = useState<boolean>(false);
    const [resultsHovered, setResultsHovered] = useState<boolean>(false);
    const [selectedResult, setSelectedResult] = useState<number>(-1);

    useEffect(() => setShadowSearchString(searchString), [searchString, setShadowSearchString]);

    useEffect(() => setSelectedResult(-1), [result.records, setSelectedResult]);

    const handleSearchStringChange = useCallback(({target: {value}}: ChangeEvent<HTMLInputElement>) => {
        setShadowSearchString(value);
        debounceCb(() => updateSearchString(value));
    }, [setShadowSearchString, updateSearchString]);

    const handleFocus = useCallback(() => {
        setSearchFieldFocused(true);
    }, [setSearchFieldFocused]);

    const handleBlur = useCallback(() => {
        setSearchFieldFocused(false);
        setSelectedResult(-1);
    }, [setSearchFieldFocused, setSelectedResult]);

    const handleSearch = useCallback(() => {
        updateFilter({searchString: shadowSearchString});
        history.push({pathname: 'search', search: getSearchPath({q: shadowSearchString})});
        searchInput.current.blur();
    }, [shadowSearchString, updateFilter, history, getSearchPath, searchInput]);

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                setSelectedResult(r => {
                    const nr = r - 1;
                    return nr >= 0 ? nr : result.records.length - 1;
                });
                break;
            case 'ArrowDown':
                e.preventDefault();
                setSelectedResult(r => {
                    const nr = r + 1;
                    return nr < result.records.length ? nr : 0;
                });
                break;
            case 'Enter':
                if (selectedResult !== -1) {
                    window.open(result.records[selectedResult].url, '_blank');
                } else {
                    handleSearch();
                }
                break;
        }
    }, [handleSearch, selectedResult, setSelectedResult, result.records]);

    const opened = searchFieldFocused || resultsHovered;

    return (
        <Box
            className={classes.root}
            onMouseMove={() => setSelectedResult(-1)}>
            <TextField
                inputRef={searchInput}
                className={classes.searchTextField}
                placeholder={strings.search}
                onFocus={handleFocus}
                onBlur={handleBlur}
                value={shadowSearchString}
                onChange={handleSearchStringChange}
                onKeyDown={handleKeyDown}
                variant="outlined"
                InputProps={{
                    endAdornment: <SearchButton onClick={handleSearch}/>,
                    inputProps: {
                        className: classes.searchInput
                    }
                }}/>
            {opened && result.records.length > 0 && (
                <List
                    className={classes.searchResults}
                    onMouseEnter={() => setResultsHovered(true)}
                    onMouseLeave={() => setResultsHovered(false)}>
                    {result.records.map(({id, title, source, url}, i) => (
                        <ListItem
                            key={id}
                            className={i === selectedResult ? classes.selectedResult : ''}
                            button
                            onClick={() => window.open(url, '_blank')}>
                            <ListItemText
                                primary={title}
                                primaryTypographyProps={{color: 'textPrimary'}}
                                secondary={<SourceLabel source={source}/>}/>
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default SearchBox;
